/**
 * Created by katarinababic on 17.6.25.
 */
import * as React from 'react';
import { observer } from 'mobx-react';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { Spinner, Progress, ProgressProps } from 'reactstrap';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Theme } from '../../Theme/Theme';
import { seriesWindow } from '../../Hooks/useAnalyticsSummary';
import { EventProcessorApi } from '../../Model/Engagement/Earn/EventProcessor';
import { useToast } from '../../Hooks/useToast';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useSaveHealthDataPointQuantity } from '../../Hooks/useHealthDataMutations';
import dayjs from 'dayjs';
import { PointsModel, PointsModelApi } from '../../Model/Engagement/Rewards/PointsModel';
import { useRootStore } from '../../Store/useRootStore';
import { useLanguage } from '../../Hooks/useLanguage';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ActivityAnalytics } from '../../Model/Activity/ActivityAnalytics';
import { HealthDataPointAnalytics } from '../../Model/HealthData/HealthDataPointAnalytics';
import { HealthDataPointAnalyticsResult } from '../../Model/HealthData/HealthDataPointAnalyticsResult';
import { createHealthDataPointQuantity } from '../../Model/HealthData/HealthDataPointQuantity';
import { HealthDataPointQuantityType } from '../../Model/HealthData/HealthDataPointQuantityType';
import { LogActivityModal } from './LogActivityModal';
import { Modal, Input as ModalInput } from 'reactstrap';
import { Translation } from '../../Components/Translation';

function useTotalTimeFormatted(value: number, language = 'de') {
  const { t } = useTrans();

  const duration = dayjs.duration(value / 1000, 'second');
  if (duration.hours() < 1) {
    return t('analytics.timeMinutes', {
      value: duration.asMinutes().toFixed(0),
      locale: language,
    });
  } else if (duration.weeks() < 2) {
    return t('analytics.timeHours', {
      value: duration.asHours().toFixed(1),
      locale: language,
    });
  } else if (duration.months() < 6) {
    return t('analytics.timeDays', {
      value: duration.asDays().toFixed(2),
      locale: language,
    });
  }

  return dayjs.duration(value ?? 0).humanize(false);
}

type ChartMetric = 'sessions' | 'steps' | 'calories' | 'duration';

const CHART_COLORS: Record<ChartMetric, { gradient: string; bar: string; barHover: string }> = {
  sessions: {
    gradient: 'linear-gradient(135deg, #00dda2 0%, #007763 100%)',
    bar: '#00dda2',
    barHover: '#007763',
  },
  steps: {
    gradient: 'linear-gradient(135deg, #007763 0%, #007763 100%)',
    bar: '#007763',
    barHover: '#007763',
  },
  calories: {
    gradient: 'linear-gradient(135deg, #F2A540 0%, #007763 100%)',
    bar: '#F2A540',
    barHover: '#007763',
  },
  duration: {
    gradient: 'linear-gradient(135deg, #00dda2 0%, #007763 100%)',
    bar: '#00dda2',
    barHover: '#007763',
  },
};

const growUp = keyframes`
  from {
    transform: scaleY(0);
    opacity: 0;
  }
  to {
    transform: scaleY(1);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
`;

// Get sensible max value for Y-axis (like mobile ChartPage.tsx)
function getSensibleMaxValue(max: number): number {
  if (max <= 0) return 10;
  if (max <= 10) return 10;

  const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
  const mostSignificantDigit = Math.ceil(max / magnitude);

  let sensibleMax;
  if (mostSignificantDigit > 5) {
    sensibleMax = 10 * magnitude;
  } else if (mostSignificantDigit > 2) {
    sensibleMax = 5 * magnitude;
  } else {
    sensibleMax = 2 * magnitude;
  }

  // Add margin above actual max
  const marginedMax = max * 1.15;
  return Math.max(sensibleMax, Math.ceil(marginedMax / magnitude) * magnitude);
}

const ChartCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;

  @media (max-width: 576px) {
    padding: 20px 16px;
  }
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const ChartTitleArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
`;

const ChartMainValue = styled.div<{ $color: string }>`
  font-size: 42px;
  font-weight: 800;
  color: ${(props) => props.$color};
  line-height: 1;
  animation: ${fadeIn} 0.5s ease;

  @media (max-width: 576px) {
    font-size: 32px;
  }
`;

const ChartUnit = styled.span`
  font-size: 18px;
  font-weight: 600;
  opacity: 0.7;
  margin-left: 6px;
`;

const ChartDateRange = styled.div`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
  margin-top: 4px;
`;

const DateNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 8px;
`;

const NavButton = styled.button<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: ${(props) => (props.$disabled ? Theme.Colors.lightestGrey : Theme.Colors.lighterGrey)};
  color: ${(props) => (props.$disabled ? Theme.Colors.lighterGrey : Theme.Colors.charcoal)};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  font-size: 16px;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${Theme.Colors.primary};
    color: white;
  }
`;

const DateRangeText = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${Theme.Colors.charcoal};
  min-width: 140px;
  text-align: center;
`;

const MetricTabs = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const MetricTab = styled.button<{ $active: boolean; $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 2px solid ${(props) => (props.$active ? props.$color : 'transparent')};
  background: ${(props) => (props.$active ? `${props.$color}15` : Theme.Colors.lightestGrey)};
  color: ${(props) => (props.$active ? props.$color : Theme.Colors.charcoal)};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => `${props.$color}20`};
    border-color: ${(props) => props.$color};
  }

  span {
    font-size: 16px;
  }
`;

const QuickLogButton = styled.button<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border-radius: 12px;
  border: 2px solid ${(props) => props.$color};
  background: ${(props) => props.$color};
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px ${(props) => props.$color}40;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${(props) => props.$color}50;
  }

  i {
    font-size: 14px;
  }
`;

const LogModalContent = styled.div`
  padding: 24px;
`;

const LogModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
  background: linear-gradient(135deg, ${Theme.Colors.primary}08, ${Theme.Colors.secondary}05);
`;

const LogModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: ${Theme.Colors.textColor};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const LogModalCloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: ${Theme.Colors.lightestGrey};
  color: ${Theme.Colors.charcoal};
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${Theme.Colors.lighterGrey};
    transform: scale(1.1);
  }
`;

const LogInputGroup = styled.div`
  margin-bottom: 20px;
`;

const LogInputLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${Theme.Colors.charcoal};
  margin-bottom: 8px;
`;

const LogInput = styled(ModalInput)`
  padding: 14px 16px;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  border: 2px solid ${Theme.Colors.lighterGrey};

  &::placeholder {
    color: ${Theme.Colors.lighterGrey};
    font-weight: 400;
    font-style: italic;
  }

  &:focus {
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 4px ${Theme.Colors.primary}15;
  }
`;

const LogModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid ${Theme.Colors.lighterGrey};
  background: ${Theme.Colors.lightestGrey}50;
`;

const LogPrimaryButton = styled.button`
  flex: 1;
  padding: 14px 24px;
  border-radius: 12px;
  border: none;
  background: #00dda2;
  color: white;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 221, 163, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background: #007763;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 112, 99, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const StyledLogModal = styled(Modal)`
  .modal-content {
    border: none;
    border-radius: 20px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    overflow: hidden;
  }
`;

const ChartContainer = styled.div`
  position: relative;
  height: 180px;
  display: flex;
  align-items: flex-end;
  gap: 2px;
  padding: 0 0 28px;
  background: repeating-linear-gradient(
    to top,
    transparent,
    transparent calc(25% - 1px),
    ${Theme.Colors.lightestGrey} calc(25% - 1px),
    ${Theme.Colors.lightestGrey} 25%
  );
  border-bottom: 2px solid ${Theme.Colors.lighterGrey};

  @media (max-width: 768px) {
    height: 160px;
    gap: 1px;
  }

  @media (max-width: 576px) {
    height: 140px;
  }
`;

const BarWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  height: 100%;
  position: relative;
`;

const Bar = styled.div<{ $height: number; $color: string; $hoverColor: string }>`
  width: 100%;
  max-width: 40px;
  height: ${(props) => Math.max(props.$height, 2)}%;
  background: ${(props) => (props.$height > 0 ? props.$color : `${props.$color}30`)};
  border-radius: 4px 4px 0 0;
  cursor: pointer;
  transform: scaleY(0);
  opacity: 0;
  transform-origin: bottom;
  animation: ${growUp} 0.4s ease-out forwards;
  transition:
    background 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    background: ${(props) => (props.$height > 0 ? props.$hoverColor : `${props.$color}30`)};
    transform: scaleY(1) scaleX(1.15);
    box-shadow: 0 4px 12px ${(props) => props.$color}50;
  }

  @media (max-width: 768px) {
    max-width: 20px;
  }

  @media (max-width: 576px) {
    max-width: 12px;
    border-radius: 2px 2px 0 0;
  }
`;

const BarLabel = styled.div`
  position: absolute;
  bottom: -24px;
  font-size: 11px;
  color: ${Theme.Colors.charcoal};
  white-space: nowrap;
  text-transform: uppercase;
`;

const BarTooltip = styled.div<{ $color: string }>`
  position: absolute;
  top: -36px;
  left: 50%;
  transform: translateX(-50%);
  background: ${(props) => props.$color};
  color: white;
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  z-index: 10;
  animation: ${fadeIn} 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid ${(props) => props.$color};
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 8px;
`;

const SkeletonBar = styled.div<{ $height: number; $delay: number }>`
  width: 100%;
  max-width: 40px;
  height: ${(props) => props.$height}%;
  background: ${Theme.Colors.lighterGrey};
  border-radius: 4px 4px 0 0;
  animation: ${pulse} 1.2s infinite ease-in-out;
  animation-delay: ${(props) => props.$delay}ms;

  @media (max-width: 768px) {
    max-width: 20px;
  }

  @media (max-width: 576px) {
    max-width: 12px;
    border-radius: 2px 2px 0 0;
  }
`;

const SkeletonValue = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${({ width }) => width || '120px'};
  height: ${({ height }) => height || '42px'};
  border-radius: 8px;

  @media (max-width: 576px) {
    height: ${({ height }) => height || '32px'};
  }
`;

const SkeletonDailyGoal = styled(SkeletonBase)`
  height: 72px;
  border-radius: 12px;
`;

const NoDataMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: ${Theme.Colors.charcoal};
  font-size: 14px;
  gap: 8px;

  span {
    font-size: 32px;
  }
`;

const SectionHeader = styled.div`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  margin-bottom: 24px;
  color: ${Theme.Colors.textColor};
`;

const StatsLayout = styled.div`
  display: flex;
  flex-direction: column;
`;

const DailyGoalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatsCard = styled.div<{ $gradient?: string }>`
  background: ${(props) => props.$gradient || 'white'};
  border-radius: 16px;
  padding: 28px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 992px) {
    padding: 20px;
  }
`;

const StatsCardTitle = styled.h3`
  font-weight: 700;
  font-size: 24px;
  line-height: 1.3;
  margin-top: 48px;
  margin-bottom: 24px;
  color: ${Theme.Colors.textColor};
`;

const RuleRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #5D6268;
  font-weight: 500;
`;

const RulePoints = styled.span`
  font-weight: 600;
`;

const PointRuleItem = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Theme.Colors.primary}40;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
`;

const ProgressContainerRule = styled.div`
  background-color: ${Theme.Colors.lightestGrey};
  border-radius: 16px;
  height: 10px;
  margin-top: 10px;
  width: 100%;
`;

interface RuleProgressBarProps extends ProgressProps {
  barColor?: string;
}
const ProgressBarRule = styled(Progress, {
  shouldForwardProp: (prop) => !['barColor'].includes(prop),
})<RuleProgressBarProps>`
  height: 100%;
  .progress-bar {
    background-color: ${(props) => props.barColor || Theme.Colors.primary};
    transition: width 0.3s ease;
  }
`;

const PeriodSwitcher = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
`;

const PeriodButton = styled.button<{ selected?: boolean }>`
  background-color: ${({ selected }) => (selected ? Theme.Colors.primary : Theme.Colors.lightestGrey)};
  color: ${({ selected }) => (selected ? Theme.Colors.white : Theme.Colors.textColor)};
  font-weight: 500;
  border: none;
  border-radius: 8px;
  padding: 10px 18px;
  margin-right: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: all 0.15s ease;

  &:last-child {
    margin-right: 0;
  }

  &:hover {
    background-color: ${({ selected }) => (selected ? Theme.Colors.primary : Theme.Colors.lightGrey)};
  }
`;

export type HomeScreenStatsProps = {};

export const HomeScreenStats: React.FC<HomeScreenStatsProps> = observer((props) => {
  const { analyticsWorkout } = useRootStore();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { t } = useTrans();
  const saveHealthData = useSaveHealthDataPointQuantity();

  const [pointsData, setPointsData] = useState<PointsModel | undefined>();
  const [pointsLoading, setPointsLoading] = useState(false);
  const [period, setPeriod] = useState<'week' | 'month' | 'year'>('month');
  const [activeMetric, setActiveMetric] = useState<ChartMetric>('steps');
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);
  const [dateRangeOffset, setDateRangeOffset] = useState(0); // 0 = current, -1 = previous, etc.

  // Quick log state
  const [logActivityModalOpen, setLogActivityModalOpen] = useState(false);
  const [healthLogModalOpen, setHealthLogModalOpen] = useState(false);
  const [healthLogType, setHealthLogType] = useState<'steps' | 'calories'>('steps');
  const [healthLogValue, setHealthLogValue] = useState('');
  const [healthLogSaving, setHealthLogSaving] = useState(false);
  const toast = useToast();

  // Calculate full period range for labels (always complete: 7 days, full month, 12 months)
  const fullPeriodRange = useMemo((): [Date, Date] => {
    const now = dayjs();
    let start: dayjs.Dayjs;
    let end: dayjs.Dayjs;

    if (period === 'week') {
      start = now.startOf('week').add(dateRangeOffset, 'week');
      end = start.endOf('week');
    } else if (period === 'month') {
      start = now.startOf('month').add(dateRangeOffset, 'month');
      end = start.endOf('month');
    } else {
      start = now.startOf('year').add(dateRangeOffset, 'year');
      end = start.endOf('year');
    }

    return [start.toDate(), end.toDate()];
  }, [period, dateRangeOffset]);

  // Calculate query range for API (capped at today for current period)
  const currentDateRange = useMemo((): [Date, Date] => {
    const now = dayjs();
    const [start, end] = fullPeriodRange;
    const endDate = dayjs(end).isAfter(now) ? now.toDate() : end;
    return [start, endDate];
  }, [fullPeriodRange]);

  // Format date range for display (always show full period)
  const dateRangeDisplay = useMemo(() => {
    const [start, end] = fullPeriodRange;
    if (period === 'week') {
      return `${dayjs(start).format('MMM D')} - ${dayjs(end).format('MMM D, YYYY')}`;
    } else if (period === 'month') {
      return dayjs(start).format('MMMM YYYY');
    } else {
      return dayjs(start).format('YYYY');
    }
  }, [fullPeriodRange, period]);

  // Check if we can go forward (not into future)
  const canGoForward = dateRangeOffset < 0;

  // Navigation handlers
  const goBack = useCallback(() => {
    setDateRangeOffset((prev) => prev - 1);
    setHoveredBar(null);
  }, []);

  const goForward = useCallback(() => {
    if (canGoForward) {
      setDateRangeOffset((prev) => prev + 1);
      setHoveredBar(null);
    }
  }, [canGoForward]);

  // Reset offset when period changes
  useEffect(() => {
    setDateRangeOffset(0);
  }, [period]);

  // Summary queries using currentDateRange so big numbers update with arrow navigation
  const { data: summary } = useQuery({
    queryKey: ['activitySummary', period, currentDateRange[0].getTime(), currentDateRange[1].getTime()],
    queryFn: () => {
      const [startDateTime, endDateTime] = currentDateRange;
      return ActivityAnalytics.summary({ startDateTime, endDateTime });
    },
  });
  const totalTimeFormatted = useTotalTimeFormatted(summary?.total?.DURATION ?? 0, language);

  const { data: healthDataSummary, refetch: refetchHealthSummary } = useQuery({
    queryKey: ['healthDataSummary', period, currentDateRange[0].getTime(), currentDateRange[1].getTime()],
    queryFn: () => {
      const [startDateTime, endDateTime] = currentDateRange;
      return HealthDataPointAnalytics.summary({ startDateTime, endDateTime, type: ['stepCount', 'activeEnergyBurned'] });
    },
  });

  // Fetch series data for charts using currentDateRange
  const { data: activitySeries = [], isLoading: activitySeriesLoading } = useQuery({
    queryKey: ['activitySeries', period, currentDateRange[0].getTime(), currentDateRange[1].getTime()],
    queryFn: () => {
      const [startDateTime, endDateTime] = currentDateRange;
      const window = seriesWindow[period] ?? 'day';
      return ActivityAnalytics.find({ startDateTime, endDateTime, window, size: 100 });
    },
  });

  const { data: healthSeries = [], isLoading: healthSeriesLoading } = useQuery<HealthDataPointAnalyticsResult[]>({
    queryKey: ['healthSeries', period, currentDateRange[0].getTime(), currentDateRange[1].getTime()],
    queryFn: () => {
      const [startDateTime, endDateTime] = currentDateRange;
      const window = seriesWindow[period] ?? 'day';
      return HealthDataPointAnalytics.find({
        startDateTime,
        endDateTime,
        window,
        type: ['stepCount', 'activeEnergyBurned'],
        size: 100,
      });
    },
  });

  const refreshPoints = useCallback(() => {
    PointsModelApi.overview().then((res) => setPointsData(res));
  }, []);

  useEffect(() => {
    setPointsLoading(true);
    PointsModelApi.overview()
      .then((res) => setPointsData(res))
      .finally(() => setPointsLoading(false));
  }, []);

  useEffect(() => {
    analyticsWorkout.setPeriodFilter(period);
  }, [analyticsWorkout, period]);

  // Reset hovered bar when metric changes
  useEffect(() => {
    setHoveredBar(null);
  }, [activeMetric, period]);

  // Quick log handlers
  const handleQuickLog = useCallback(() => {
    if (activeMetric === 'sessions' || activeMetric === 'duration') {
      setLogActivityModalOpen(true);
    } else if (activeMetric === 'steps') {
      setHealthLogType('steps');
      setHealthLogValue('');
      setHealthLogModalOpen(true);
    } else if (activeMetric === 'calories') {
      setHealthLogType('calories');
      setHealthLogValue('');
      setHealthLogModalOpen(true);
    }
  }, [activeMetric]);

  const handleHealthLogSave = useCallback(async () => {
    const value = parseFloat(healthLogValue);
    if (isNaN(value) || value <= 0) return;

    setHealthLogSaving(true);
    try {
      const hdType =
        healthLogType === 'steps'
          ? HealthDataPointQuantityType.stepCount
          : HealthDataPointQuantityType.activeEnergyBurned;
      const healthDataPoint = createHealthDataPointQuantity({
        type: hdType,
        value: value,
        startDate: dayjs().toISOString(),
        endDate: dayjs().toISOString(),
      } as any);
      await saveHealthData.mutateAsync(healthDataPoint);
      toast.showToast(t('health.logged.success', { defaultValue: 'Health data logged successfully!' }), 'success');
      setHealthLogModalOpen(false);
      refetchHealthSummary();
      // Emit health_data_changed for live points update (backend polls every 5 min)
      EventProcessorApi.submitEvent({
        event: 'health_data_changed',
        requestId: `health-${hdType}-${Date.now()}`,
        data: { type: hdType, value, [hdType]: value },
        eventDateTime: new Date(),
      })
        .then(() => refreshPoints())
        .catch((err) => console.error('Failed to emit health_data_changed:', err));
    } catch (err) {
      console.error('Error saving health data:', err);
      toast.showToast(
        t('health.logged.error', { defaultValue: 'Failed to log health data. Please try again.' }),
        'error',
      );
    } finally {
      setHealthLogSaving(false);
    }
  }, [healthLogType, healthLogValue, toast, t, refetchHealthSummary, refreshPoints, saveHealthData]);

  const handleActivityLogged = useCallback(() => {
    refetchHealthSummary();
    // Emit activity_logged for live points update
    EventProcessorApi.submitEvent({
      event: 'activity_logged',
      requestId: `activity-manual-${Date.now()}`,
      eventDateTime: new Date(),
    })
      .then(() => refreshPoints())
      .catch((err) => console.error('Failed to emit activity_logged:', err));
  }, [refetchHealthSummary, refreshPoints]);

  const activitiesValue = summary?.totalCount ?? 0;
  const stepsValue = Math.round(healthDataSummary?.stepCount ?? 0).toLocaleString();
  const durationValue = totalTimeFormatted.split(' ')[0];
  const kcalValue = Math.round(healthDataSummary?.activeEnergyBurned ?? 0).toLocaleString();

  // Format chart data based on active metric (like mobile ChartPage.tsx)
  const chartData = useMemo(() => {
    // Generate labels for FULL period (always 7 days, all month days, 12 months)
    const [startDate, endDate] = fullPeriodRange;
    const labels: { label: string; key: string }[] = [];
    let current = dayjs(startDate);
    const end = dayjs(endDate);

    if (period === 'week') {
      while (!current.isAfter(end, 'day')) {
        labels.push({ label: current.format('dd'), key: current.format('YYYY-MM-DD') });
        current = current.add(1, 'day');
      }
    } else if (period === 'month') {
      while (!current.isAfter(end, 'day')) {
        const label = (current.date() - 1) % 5 === 0 ? current.format('D') : '';
        labels.push({ label, key: current.format('YYYY-MM-DD') });
        current = current.add(1, 'day');
      }
    } else {
      while (!current.isAfter(end, 'month')) {
        labels.push({ label: current.format('MMM').charAt(0), key: current.format('YYYY-MM') });
        current = current.add(1, 'month');
      }
    }

    // Get date key format
    const getDateKey = (dateString: string): string => {
      if (period === 'week' || period === 'month') {
        return dayjs(dateString).format('YYYY-MM-DD');
      }
      return dayjs(dateString).format('YYYY-MM');
    };

    // Get value data from API response
    let valueData: { key: string; value: number }[] = [];

    switch (activeMetric) {
      case 'sessions':
        valueData = activitySeries.map((item) => ({
          key: getDateKey(item.id),
          value: item.totalCount,
        }));
        break;
      case 'steps':
        valueData = healthSeries.map((item) => ({
          key: getDateKey(item.id),
          value: Math.round(item.total?.stepCount ?? 0),
        }));
        break;
      case 'calories':
        valueData = healthSeries.map((item) => ({
          key: getDateKey(item.id),
          value: Math.round(item.total?.activeEnergyBurned ?? 0),
        }));
        break;
      case 'duration':
        valueData = activitySeries.map((item) => ({
          key: getDateKey(item.id),
          value: Math.round((item.total?.DURATION ?? 0) / 60000),
        }));
        break;
    }

    // Map labels to values, filling 0 for missing dates
    return labels.map(({ label, key }) => {
      const found = valueData.find((v) => v.key === key);
      return {
        label,
        date: key,
        value: found?.value ?? 0,
      };
    });
  }, [activeMetric, activitySeries, healthSeries, fullPeriodRange, period]);

  const maxValue = useMemo(() => {
    const dataMax = Math.max(...chartData.map((d) => d.value), 0);
    return getSensibleMaxValue(dataMax);
  }, [chartData]);

  const getMetricTotal = useCallback(() => {
    switch (activeMetric) {
      case 'sessions':
        return activitiesValue;
      case 'steps':
        return stepsValue;
      case 'calories':
        return kcalValue;
      case 'duration':
        return durationValue;
    }
  }, [activeMetric, activitiesValue, stepsValue, kcalValue, durationValue]);

  const getMetricUnit = useCallback(() => {
    switch (activeMetric) {
      case 'sessions':
        return t('home.sessionsUnit', { defaultValue: 'sessions' });
      case 'steps':
        return t('home.stepsUnit', { defaultValue: 'steps' });
      case 'calories':
        return t('home.caloriesUnit', { defaultValue: 'kcal' });
      case 'duration':
        return t('home.durationUnit', { defaultValue: 'min' });
    }
  }, [activeMetric, t]);

  const formatBarValue = useCallback(
    (value: number) => {
      if (activeMetric === 'duration') {
        if (value >= 60) return `${(value / 60).toFixed(1)}h`;
        return `${value}m`;
      }
      return value.toLocaleString();
    },
    [activeMetric],
  );

  const isChartLoading =
    activeMetric === 'sessions' || activeMetric === 'duration' ? activitySeriesLoading : healthSeriesLoading;

  return (
    <span>
      <SectionHeader>
        <Translation
          i18nKey="home.statistics"
          defaultValue={`<span style="color: #00dda2">Your</span> statistics`}
        />
      </SectionHeader>
      <StatsLayout>
        {/* Chart Section */}
        <ChartCard>
          <ChartHeader>
            <ChartTitleArea>
              <ChartMainValue $color={CHART_COLORS[activeMetric].bar} style={isChartLoading ? { opacity: 0 } : undefined}>
                {getMetricTotal()}
                <ChartUnit>{getMetricUnit()}</ChartUnit>
              </ChartMainValue>
              {isChartLoading && (
                <SkeletonValue width="140px" style={{ position: 'absolute', top: 0, left: 0 }} />
              )}
              <ChartDateRange>
                {hoveredBar !== null && chartData[hoveredBar]
                  ? dayjs(chartData[hoveredBar].date).format(
                      period === 'week' ? 'ddd, MMM D' : period === 'month' ? 'MMM D' : 'MMMM YYYY',
                    )
                  : dateRangeDisplay}
              </ChartDateRange>
              <DateNavigation>
                <NavButton onClick={goBack} title={t('home.previousPeriod', { defaultValue: 'Previous' })}>
                  ←
                </NavButton>
                <DateRangeText>{dateRangeDisplay}</DateRangeText>
                <NavButton
                  onClick={goForward}
                  $disabled={!canGoForward}
                  disabled={!canGoForward}
                  title={t('home.nextPeriod', { defaultValue: 'Next' })}
                >
                  →
                </NavButton>
              </DateNavigation>
            </ChartTitleArea>
            <MetricTabs>
              <MetricTab
                $active={activeMetric === 'sessions'}
                $color={CHART_COLORS.sessions.bar}
                onClick={() => setActiveMetric('sessions')}
              >
                {t('home.activities', { defaultValue: 'Activities' })}
              </MetricTab>
              <MetricTab
                $active={activeMetric === 'steps'}
                $color={CHART_COLORS.steps.bar}
                onClick={() => setActiveMetric('steps')}
              >
                {t('home.steps', { defaultValue: 'Steps' })}
              </MetricTab>
              <MetricTab
                $active={activeMetric === 'calories'}
                $color={CHART_COLORS.calories.bar}
                onClick={() => setActiveMetric('calories')}
              >
                {t('home.calories', { defaultValue: 'Calories' })}
              </MetricTab>
              <MetricTab
                $active={activeMetric === 'duration'}
                $color={CHART_COLORS.duration.bar}
                onClick={() => setActiveMetric('duration')}
              >
                {t('home.activeTime', { defaultValue: 'Time' })}
              </MetricTab>
              <QuickLogButton $color={CHART_COLORS[activeMetric].bar} onClick={handleQuickLog}>
                <i className="bi bi-plus-lg" />
                {activeMetric === 'sessions' || activeMetric === 'duration'
                  ? t('home.logActivity', { defaultValue: 'Log Activity' })
                  : activeMetric === 'steps'
                    ? t('home.logSteps', { defaultValue: 'Log Steps' })
                    : t('home.logCalories', { defaultValue: 'Log Calories' })}
              </QuickLogButton>
            </MetricTabs>
          </ChartHeader>

          {isChartLoading ? (
            <ChartContainer>
              {Array.from({ length: period === 'week' ? 7 : period === 'month' ? 30 : 12 }).map((_, index, arr) => (
                <BarWrapper key={index}>
                  <SkeletonBar
                    $height={25 + Math.sin(index * 0.9) * 20 + ((index * 7) % 30)}
                    $delay={(index * 137) % 600}
                  />
                  <BarLabel>
                    {period === 'week'
                      ? ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][index]
                      : period === 'year'
                        ? ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][index]
                        : index % 5 === 0
                          ? index + 1
                          : ''}
                  </BarLabel>
                </BarWrapper>
              ))}
            </ChartContainer>
          ) : chartData.length === 0 ? (
            <NoDataMessage>
              {t('home.noChartData', { defaultValue: 'No data for this period' })}
            </NoDataMessage>
          ) : (
            <ChartContainer>
              {chartData.map((item, index) => (
                <BarWrapper
                  key={item.date}
                  onMouseEnter={() => setHoveredBar(index)}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  {hoveredBar === index && item.value > 0 && (
                    <BarTooltip $color={CHART_COLORS[activeMetric].bar}>{formatBarValue(item.value)}</BarTooltip>
                  )}
                  <Bar
                    $height={(item.value / maxValue) * 100}
                    $color={CHART_COLORS[activeMetric].bar}
                    $hoverColor={CHART_COLORS[activeMetric].barHover}
                  />
                  <BarLabel>{item.label}</BarLabel>
                </BarWrapper>
              ))}
            </ChartContainer>
          )}

          {/* Period Selector */}
          <PeriodSwitcher style={{ marginTop: 24, marginBottom: 0 }}>
            {(['week', 'month', 'year'] as const).map((p) => (
              <PeriodButton key={p} selected={period === p} onClick={() => setPeriod(p)}>
                {t(`home.${p}`, { defaultValue: p.charAt(0).toUpperCase() + p.slice(1) })}
              </PeriodButton>
            ))}
          </PeriodSwitcher>
        </ChartCard>

        {/* Daily Goals Card - Full width */}
        {pointsData?.dailyRules.filter((r) => r.completed || r.canBeCompleted).length || pointsLoading ? (<>
          <StatsCardTitle>
            <Translation
              i18nKey="home.dailyPoints"
              defaultValue={`<span style="color: #00dda2">Already</span> {{points}} today`}
              points={pointsData?.collectedPoints ?? 0}
            />    
          </StatsCardTitle>
          <StatsCard style={{ position: 'relative' }}>
            {pointsLoading && (
              <DailyGoalsGrid style={{ position: 'absolute', top: 52, left: 28, right: 28 }}>
                {Array.from({ length: 3 }).map((_, i) => (
                  <SkeletonDailyGoal key={i} style={{ animationDelay: `${i * 100}ms` }} />
                ))}
              </DailyGoalsGrid>
            )}
            <DailyGoalsGrid style={{ opacity: pointsLoading ? 0 : 1, transition: 'opacity 0.2s', minHeight: pointsLoading ? 72 : 'auto' }}>
              {pointsData?.dailyRules
                .filter((r) => r.completed || r.canBeCompleted)
                .map((rule) => {
                  let ruleLink: string | undefined;
                  switch (rule.appActionLink) {
                    case 'https://coach.kinastic.com/explore-tab/mental':
                      ruleLink = '/mental-health';
                      break;
                    case 'https://coach.kinastic.com/explore-tab/nutrition':
                      ruleLink = '/nutrition';
                      break;
                    case 'https://coach.kinastic.com/explore-tab/academy':
                      ruleLink = '/academy';
                      break;
                    case 'https://coach.kinastic.com/log-activity':
                      ruleLink = '/move';
                      break;
                    case 'https://coach.kinastic.com/feed':
                      ruleLink = '/academy/0bfd3bb9-21a3-49aa-bbce-13e1bb39d3c8';
                      break;
                    default:
                      ruleLink = undefined;
                  }
                  const clickable = !!ruleLink && !rule.completed;
                  const percent =
                    rule.totalExperiencePoints > 0
                      ? (rule.collectedExperiencePoints / rule.totalExperiencePoints) * 100
                      : 0;
                  return (
                    <PointRuleItem
                      key={rule.id}
                      onClick={() => clickable && navigate(ruleLink!)}
                      style={{ cursor: clickable ? 'pointer' : 'default' }}
                    >
                      <RuleRow>
                        <span>{rule.getName(language)}</span>
                        <RulePoints>{rule.collectedExperiencePoints}/{rule.totalExperiencePoints}</RulePoints>
                      </RuleRow>
                      <ProgressContainerRule>
                        <ProgressBarRule
                          value={percent}
                          max="100"
                          barColor={rule.completed ? '#10B981' : Theme.Colors.primary}
                        />
                      </ProgressContainerRule>
                    </PointRuleItem>
                  );
                })}
            </DailyGoalsGrid>
          </StatsCard>
        </>) : null}
      </StatsLayout>

      {/* Log Activity Modal */}
      <LogActivityModal
        isOpen={logActivityModalOpen}
        onClose={() => setLogActivityModalOpen(false)}
        onActivityLogged={handleActivityLogged}
      />

      {/* Health Data Log Modal (Steps/Calories) */}
      <StyledLogModal isOpen={healthLogModalOpen} toggle={() => setHealthLogModalOpen(false)} centered size="sm">
        <LogModalHeader>
          <LogModalTitle>
            {healthLogType === 'steps'
              ? t('home.logSteps', { defaultValue: 'Log Steps' })
              : t('home.logCalories', { defaultValue: 'Log Calories' })}
          </LogModalTitle>
          <LogModalCloseButton onClick={() => setHealthLogModalOpen(false)}>×</LogModalCloseButton>
        </LogModalHeader>
        <LogModalContent>
          <LogInputGroup>
            <LogInputLabel>
              {healthLogType === 'steps'
                ? t('home.stepsCount', { defaultValue: 'Number of steps' })
                : t('home.caloriesAmount', { defaultValue: 'Calories burned' })}
            </LogInputLabel>
            <LogInput
              type="number"
              placeholder={healthLogType === 'steps' ? '5000' : '500'}
              value={healthLogValue}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHealthLogValue(e.target.value)}
              autoFocus
            />
          </LogInputGroup>
        </LogModalContent>
        <LogModalFooter>
          <LogPrimaryButton
            onClick={handleHealthLogSave}
            disabled={healthLogSaving || !healthLogValue || parseFloat(healthLogValue) <= 0}
          >
            {healthLogSaving ? (
              <Spinner size="sm" color="light" />
            ) : (
              <>
                <i className="bi bi-check2" /> {t('home.save', { defaultValue: 'Save' })}
              </>
            )}
          </LogPrimaryButton>
        </LogModalFooter>
      </StyledLogModal>
    </span>
  );
});
